/* @HEADER
 * @version 1.0.2522 | 2026-10-03
 * @purpose The authenticated route table as data (ZVX-DEV-6136, App.tsx-by-meaning S3): each route names its screen, whether it loads lazily, and what it needs from the host. A component-free module so the table can be imported by tests and tools; AuthedRouteScreen.tsx renders from it.
 * @sidecar authedRouteTable.tsx.header.md
 * @layer app | @hex adapter | @ctx app
 * @public false
 * @edit careful
 */

// SpecRefs: ZVX-DEV-7233, ZVX-DEV-6136, ZVX-DEV-1454, ZVX-DEV-2261

import type { ComponentProps, ReactNode } from 'react'
import type { Route } from './routes'
import { AdminPage } from '../features/admin/public-api'
import { SettingsPage } from '../features/settings/SettingsPage'
import { OkrPage } from '../features/okr/OkrPage'
import { EventsScreen } from '../features/events/EventsScreen'
import { DiaryPage } from '../features/diary/public-api'
import { StructurePage } from '../features/structure/StructurePage'
import { ZvenixWikiScreen } from '../features/wiki/ZvenixWikiScreen'
import { ZvenixCanvasScreen } from '../features/canvas/ZvenixCanvasScreen'
import { WorkspaceSettingsPage } from '../features/workspace/public-api'
import { AnalyticsPage } from './lazyScreens'
import { SharedWithMeSection } from '../features/content-access/SharedWithMeSection'

/** The pair the organization overlay is rendered with — the page's own prop types, so the two cannot drift. */
export type WorkspaceSettingsContext = Pick<ComponentProps<typeof WorkspaceSettingsPage>, 'activeWorkspace' | 'currentUser'>


/** What the host hands every screen — nothing more than the switch used to close over. */
export interface AuthedScreenHost {
  canGoBack: boolean
  onGoBack: () => void
  onNavigate: (route: Route) => void
  /** The tasks route: the space view the host composes from its own state. */
  spaceView: ReactNode
  /** The organization overlay reads its context at render time — the snapshot may have changed since mount. */
  readWorkspaceSettingsContext: () => WorkspaceSettingsContext
  onWorkspaceDeleted: () => void
}

export interface ScreenSpec {
  render: (host: AuthedScreenHost) => ReactNode
  /** Lazy screens render inside Suspense with the shared fallback. */
  lazy?: boolean
}

/**
 * The route table. Adding a route means adding a row here and a member of
 * `Route`; TypeScript refuses a table that misses one.
 */
export const AUTHED_ROUTE_SCREENS: Record<Route, ScreenSpec> = {
  tasks: { render: (h) => h.spaceView },
  'shared-with-me': { render: () => <SharedWithMeSection presentation="screen" /> },
  canvas: { lazy: true, render: (h) => <ZvenixCanvasScreen canGoBack={h.canGoBack} onGoBack={h.onGoBack} /> },
  structure: { render: () => <StructurePage /> },
  wiki: { render: () => <ZvenixWikiScreen /> },
  analytics: { lazy: true, render: (h) => <AnalyticsPage onNavigate={h.onNavigate} /> },
  okr: { render: (h) => <OkrPage onNavigate={h.onNavigate} /> },
  events: { render: (h) => <EventsScreen onNavigate={h.onNavigate} /> },
  diary: { render: (h) => <DiaryPage onNavigate={h.onNavigate} /> },
  admin: { render: () => <AdminPage /> },
  settings: { render: (h) => <SettingsPage onNavigate={h.onNavigate} /> },
}
